import React, { useState } from 'react'
import NamingPatternField from './NamingPatternField'
import SoakDaysInput, { SOAK_MAX, KEEP_OPTIONS, soakDaysError, soakDaysValue } from './SoakDaysInput'
import AdvancedSettings, {
  emptyRequirements,
  hardwareSummary,
  DEFAULT_RESTART_BEHAVIOUR,
  RESTART_BEHAVIOURS,
  defaultReturnCodes,
} from './AdvancedSettings'
import type { IntuneRequirements, RestartBehaviour, ReturnCode } from './AdvancedSettings'
import { renderNamingPattern, intentToken, usesDeploymentName } from '../utils/namingPattern'
import { DEPLOYMENT_NAME_TOKEN, validatePattern } from '../utils/naming'
import { intuneSummary } from '../utils/templateSummary'
import { Stepper, Select, Toggle, Checkbox, useStepper } from './ui'
import AssignmentBlade, {
  AssignmentRow,
  AssignmentGroup,
  AssignmentReviewTable,
  assignmentReviewRows,
  newAssignmentRow,
} from './AssignmentBlade'
import {
  createIntuneTemplate,
  updateIntuneTemplate,
  IntuneDeploymentTemplate,
} from '../services/api'
import {
  DEFAULT_APP_PATTERN,
  DEFAULT_GROUP_PATTERN,
  DEFAULT_UPDATE_APP_PATTERN,
  DEFAULT_UPDATE_GROUP_PATTERN,
  SAMPLE_VALUES,
  type NameValues,
} from '../utils/naming'
import { errorText } from '../utils/errorText'

/**
 * Intune deployment-template wizard (016-template-model §11.3). SEVEN steps, in the mock's own
 * order (its DeploymentTemplateWizard.tsx:68-76): General, Metadata, Advanced, Install,
 * Update-only app, Automated updates, Overview.
 *
 * Assembles already-tested pieces rather than re-implementing their rules: NamingPatternField
 * owns token scoping and pattern validation, AssignmentBlade owns the audience rule and the
 * Required-only deadline, and both are parity-tested against the backend. This component's own
 * job is the metadata fields, the automated-update switches, and shaping the save payload.
 *
 * The ADVANCED step carries two of Intune's own blades — Requirements, and the return-code half
 * of Program. Both are detail most templates never touch, and giving each its own step would have
 * made a seven-step wizard nine.
 *
 * A template publishes up to TWO Intune app objects from one package: the app itself, and the
 * update-only app that patches devices which already have it and never introduces it. No native
 * Intune intent exists for the second, so it is a requirement rule checking for an existing
 * install (spec §6.2). Each is a separate Graph app with its own assignments, which is why steps
 * 4 and 5 are two independent blades.
 *
 * What the template does NOT carry: app name, publisher and description. They are per-app facts
 * read from the package at packaging time, so step 2 shows them disabled — the boundary is
 * visible rather than merely documented.
 */

/** Categories come from the TENANT (GET /integrations/intune/categories), never from a list in
 *  here. Intune has no fixed category vocabulary — the set is per-tenant data, Intune's built-ins
 *  plus whatever the tenant has added — so any hardcoded list offers names the tenant does not
 *  have. Publishing does not create them either, so an unknown name is simply not applied. */

/** win32LobApp.developer is who PACKAGED the app, not the software vendor -- which is always us.
 *  The reference seeds its own name here for the same reason. */
const DEFAULT_DEVELOPER = 'Splashtop Agentic Packr'

const STEPS = [
  'General',
  'Metadata',
  // V-1: third, as the mock orders it (its DeploymentTemplateWizard.tsx:71-77). It was missing
  // entirely — 18 controls that rendered nowhere — and the `requirements` column behind it has
  // existed unused since migration 036.
  'Advanced',
  'Install',
  'Update-only app',
  'Automated updates',
  'Overview',
] as const

/** A template is not bound to one app, so every name it shows keeps its tokens: the pattern IS
 *  the answer at this point, and a concrete name would assert an app nobody chose. The one
 *  substitution made anywhere is {Intent}, which the template itself decides. {DeploymentName}
 *  is given as itself: left unfilled it would render empty and drop out of the name. */
const NO_APP_YET = { deploymentname: DEPLOYMENT_NAME_TOKEN } as const

const inputCls =
  'w-full px-3 py-2 bg-theme-primary border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-60 text-sm'

/** `error` replaces the hint rather than stacking under it: the field is wrong, and the example of
 *  a right answer is no longer the thing to read. */
const Field: React.FC<{
  label: string
  hint?: string
  error?: string | null
  children: React.ReactNode
}> = ({ label, hint, error, children }) => (
  <div className="mb-4">
    <label className="label">{label}</label>
    {children}
    {error ? (
      <p className="mt-1 text-xs text-red-400">{error}</p>
    ) : (
      hint && <p className="mt-1 text-xs text-theme-text-tertiary">{hint}</p>
    )}
  </div>
)


/** One label/value row of the Overview — the mock's `Line` (its DeploymentTemplateWizard.tsx:1086). */
const Line: React.FC<{ k: string; v: React.ReactNode; mono?: boolean }> = ({ k, v, mono }) => (
  <div className="flex items-start justify-between gap-4 border-b border-theme-border-primary pb-2 last:border-0">
    <dt className="shrink-0 text-theme-text-tertiary">{k}</dt>
    <dd className={`text-right font-medium text-theme-text-primary ${mono ? 'font-mono text-[13px]' : ''}`}>
      {v}
    </dd>
  </div>
)

/** `dl` off for a section whose body is a table: a table is not a list of label/value pairs, and a
 *  <dl> accepts only dt/dd. */
const OvSection: React.FC<{
  title: string
  children: React.ReactNode
  note?: string
  dl?: boolean
}> = ({ title, children, note, dl = true }) => (
  <section className="mb-5">
    <p className="mb-2 text-sm font-medium text-theme-text-primary">{title}</p>
    {dl ? <dl className="space-y-2 text-sm">{children}</dl> : children}
    {note && <p className="mt-2 text-[12px] text-theme-text-tertiary">{note}</p>}
  </section>
)


export interface Draft {
  name: string
  description: string
  notes: string
  owner: string
  developer: string
  categories: string[]
  featured: boolean
  informationUrl: string
  privacyUrl: string
  allowUninstallFromCompanyPortal: boolean
  appPattern: string
  updateAppPattern: string
  groupPattern: string
  updateGroupPattern: string
  requirements: IntuneRequirements
  restartBehaviour: RestartBehaviour
  returnCodes: ReturnCode[]
  appAssignments: AssignmentRow[]
  /** No install app object is created at publish. Only the update-only app is published, so the
   *  app is patched where it is already installed and never introduced. */
  skipInstall: boolean
  updateEnabled: boolean
  /** Whether the update-only app recognises copies of the product this platform did not install
   *  (matched by their Add/Remove Programs name). Gates its requirement AND its detection rule -
   *  they answer different questions about the same population and must never disagree. */
  updateMatchUnmanaged: boolean
  updateAssignments: AssignmentRow[]
  followNewVersions: boolean
  soakDays: number
  keepVersions: number
  supersede: boolean
  uninstallPrevious: boolean
  /** Always on -- auto-update is the reason to set supersedence at all, so the Automated updates
   *  step states it rather than offering it. Kept on the draft so the Graph mapping stays
   *  explicit, exactly as the reference does. */
  autoUpdateFromCompanyPortal: boolean
}

/** ONE Required row on ALL DEVICES — what the step's own recommendation asks for. The update-only
 *  object cannot introduce the app (a requirement rule limits it to devices that already have
 *  it), so a narrower target only leaves machines unpatched.
 *
 *  NEVER a group out of the tenant directory. This is the one assignment where a wrong audience
 *  is silent: a group IS selected, so nothing is flagged, and the patch app reaches that group
 *  while the rest of the estate goes unpatched. */
const defaultUpdateAssignments = (): AssignmentRow[] => [newAssignmentRow('required', 'allDevices')]

export const emptyDraft = (): Draft => ({
  name: '',
  description: '',
  notes: '',
  owner: '',
  developer: DEFAULT_DEVELOPER,
  categories: [],
  featured: false,
  informationUrl: '',
  privacyUrl: '',
  allowUninstallFromCompanyPortal: true,
  appPattern: DEFAULT_APP_PATTERN,
  updateAppPattern: DEFAULT_UPDATE_APP_PATTERN,
  groupPattern: DEFAULT_GROUP_PATTERN,
  updateGroupPattern: DEFAULT_UPDATE_GROUP_PATTERN,
  requirements: emptyRequirements(),
  // spec 6.4. Published to Intune exactly; the control states what each value does on the device.
  restartBehaviour: DEFAULT_RESTART_BEHAVIOUR,
  returnCodes: defaultReturnCodes(),
  // NO assignment, and no preselected audience. A seeded Required row defaulting to every device
  // meant a template saved without opening the assignment step published the app to the whole
  // estate. The blade's add buttons each state who the row is for, so every audience is chosen.
  appAssignments: [],
  // Off: creating the app object is what a deployment normally does, and an administrator who
  // wants only to patch an existing estate has to say so.
  skipInstall: false,
  // On by default: patching a broad population is safe precisely because it installs nothing new.
  updateEnabled: true,
  // On, because it is what the object already did: its requirement rule has always accepted the
  // product's own Add/Remove Programs entry. Defaulting off would narrow the updater to installs
  // this platform performed, which is the population that needs it least.
  updateMatchUnmanaged: true,
  updateAssignments: defaultUpdateAssignments(),
  followNewVersions: true,
  soakDays: 7,
  keepVersions: 3,
  supersede: true,
  uninstallPrevious: false,
  autoUpdateFromCompanyPortal: true,
})

export function draftFrom(t: IntuneDeploymentTemplate): Draft {
  const c = t.config ?? ({} as any)
  const d = emptyDraft()
  d.name = t.name
  d.description = t.description ?? ''
  d.notes = c.intuneMetadata?.notes ?? ''
  d.owner = c.intuneMetadata?.owner ?? ''
  d.developer = c.intuneMetadata?.developer ?? DEFAULT_DEVELOPER
  d.categories = c.intuneMetadata?.categories ?? []
  d.featured = !!c.intuneMetadata?.featured
  d.informationUrl = c.intuneMetadata?.informationUrl ?? ''
  d.privacyUrl = c.intuneMetadata?.privacyUrl ?? ''
  d.allowUninstallFromCompanyPortal = c.allowUninstallFromCompanyPortal ?? true
  d.appPattern = c.naming?.appPattern ?? d.appPattern
  d.updateAppPattern = c.naming?.updateAppPattern ?? d.updateAppPattern
  d.groupPattern = c.naming?.groupPattern ?? d.groupPattern
  d.updateGroupPattern = c.naming?.updateGroupPattern ?? d.updateGroupPattern
  // Merged onto the defaults so a template saved before this step existed opens with a valid
  // minimumOs rather than a blank the validator would reject.
  if (c.requirements) d.requirements = { ...d.requirements, ...(c.requirements as any) }
  if ((c as any).restartBehaviour) d.restartBehaviour = (c as any).restartBehaviour
  if ((c as any).returnCodes?.length) d.returnCodes = (c as any).returnCodes
  if (c.appAssignments?.length) d.appAssignments = c.appAssignments as AssignmentRow[]
  d.skipInstall = !!(c as any).skipInstall
  d.updateEnabled = c.updateOnly?.enabled ?? true
  // A template saved before this setting existed opens with the reach it actually had.
  d.updateMatchUnmanaged = (c.updateOnly as any)?.matchUnmanaged ?? true
  if (c.updateOnly?.assignments?.length) d.updateAssignments = c.updateOnly.assignments as AssignmentRow[]
  d.followNewVersions = c.automatedUpdates?.followNewVersions ?? true
  d.soakDays = c.automatedUpdates?.soakDays ?? 7
  d.keepVersions = c.automatedUpdates?.keepVersions ?? 3
  d.supersede = c.automatedUpdates?.supersede ?? true
  d.uninstallPrevious = !!c.automatedUpdates?.uninstallPrevious
  return d
}

export function toConfig(d: Draft) {
  return {
    intuneMetadata: {
      notes: d.notes,
      owner: d.owner,
      developer: d.developer,
      categories: d.categories,
      featured: d.featured,
      informationUrl: d.informationUrl,
      privacyUrl: d.privacyUrl,
    },
    naming: {
      appPattern: d.appPattern,
      updateAppPattern: d.updateAppPattern,
      groupPattern: d.groupPattern,
      updateGroupPattern: d.updateGroupPattern,
    },
    requirements: d.requirements,
    restartBehaviour: d.restartBehaviour,
    returnCodes: d.returnCodes,
    appAssignments: d.appAssignments,
    skipInstall: d.skipInstall,
    updateOnly: {
      enabled: d.updateEnabled,
      matchUnmanaged: d.updateMatchUnmanaged,
      assignments: d.updateEnabled ? d.updateAssignments : [],
    },
    automatedUpdates: {
      followNewVersions: d.followNewVersions,
      soakDays: d.soakDays,
      keepVersions: d.keepVersions,
      supersede: d.supersede,
      uninstallPrevious: d.uninstallPrevious,
      autoUpdateFromCompanyPortal: d.autoUpdateFromCompanyPortal,
    },
    allowUninstallFromCompanyPortal: d.allowUninstallFromCompanyPortal,
  }
}

export interface DeploymentTemplateWizardProps {
  template?: IntuneDeploymentTemplate
  groups?: AssignmentGroup[]
  /** Intune assignment filters, so the blade's filter picker has a source. */
  filters?: { id: string; name: string }[]
  /** The tenant's Company Portal category names, read from Intune. */
  categories?: string[]
  /** False when Intune could not be asked. An empty list then means "unknown", not "none", and
   *  the control says which — a picker that shows nothing looks like a tenant with nothing. */
  categoriesAvailable?: boolean
  /** The list is still being read. Distinct from both of the above for the same reason. */
  categoriesLoading?: boolean
  onClose: () => void
  onSaved: () => void
  /**
   * F-7 APPLY MODE, the delivery twin of the UX wizard's. When set the wizard does not save: it
   * hands the resolved config back, and the caller carries it on ONE publish as
   * intune_inline_config. The template is never rewritten — it is shared, and editing it would
   * rewrite the history of every app already published from it (spec §7a.1).
   *
   * General is dropped here: a name and description belong to a saved template, and in apply mode
   * there is nothing to save them under.
   */
  onApply?: (config: any) => void
  /**
   * Naming-token values of the app this template is being applied to, so the app-name and
   * group-name previews show that app. Omitted when a template is edited on its own, and the
   * previews then use the sample app.
   */
  previewValues?: NameValues
}

const DeploymentTemplateWizard: React.FC<DeploymentTemplateWizardProps> = ({
  template,
  groups = [],
  filters = [],
  categories = [],
  categoriesAvailable = true,
  categoriesLoading = false,
  onClose,
  onSaved,
  onApply,
  previewValues,
}) => {
  const namePreview = previewValues ? { ...SAMPLE_VALUES, ...previewValues } : undefined
  /** Group names on the assignment rows: this app's values when there is one, the pattern's
   *  tokens otherwise, and {DeploymentName} as written either way. */
  const rowPreview = { ...previewValues, ...NO_APP_YET }
  const [d, setD] = useState<Draft>(() => (template ? draftFrom(template) : emptyDraft()))
  const steps = React.useMemo<readonly string[]>(
    () => (onApply ? STEPS.filter((x) => x !== 'General') : STEPS),
    [onApply],
  )
  const [stepIdx, setStepIdx] = useState(0)
  /** The template already exists — every step is filled in, so none of them is "not yet done". */
  const isEdit = !!template
  /** Which steps have been reached, for a template being created. Seeded with the first, which is
   *  not General in apply mode. */
  const [visited, setVisited] = useState<Set<string>>(() => new Set([steps[0]]))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /** The General step's own rule, shown on the NAME field. A field rule belongs next to the field,
   *  not in the shared list at the foot of the wizard. */
  const [nameError, setNameError] = useState<string | null>(null)
  /** The soak field's raw text, because a number input is empty for a keystroke whenever it is
   *  retyped and `soakDays` has no value that means "mid-edit". The draft only takes a whole number
   *  in range; anything else keeps the last good value and names itself as the reason Save is off,
   *  rather than being silently rounded into something the admin did not choose. */
  const [soakText, setSoakText] = useState(() => String(d.soakDays))

  /** A name this template already carries that the tenant no longer has. Kept VISIBLE rather than
   *  dropped: a saved template written before the picker read Intune can carry one, and silently
   *  removing it on open would hide the fact that it never applied. Matched case-insensitively,
   *  as the binder matches it. */
  const categoriesKnown = categoriesAvailable && !categoriesLoading
  const missingCategories = React.useMemo(() => {
    if (!categoriesKnown) return []
    const known = new Set(categories.map((c) => c.trim().toLowerCase()))
    return d.categories.filter((c) => !known.has(c.trim().toLowerCase()))
  }, [categories, categoriesKnown, d.categories])

  /** The tenant's categories, plus any stale name this template still carries. Until the tenant's
   *  list is known, only the template's own names are offered — nothing here may invent one. */
  const categoryOptions = React.useMemo(
    () =>
      categoriesKnown
        ? [
            ...categories.map((name) => ({ name, inTenant: true })),
            ...missingCategories.map((name) => ({ name, inTenant: false })),
          ]
        : d.categories.map((name) => ({ name, inTenant: true })),
    [categories, categoriesKnown, missingCategories, d.categories],
  )

  const safeIdx = Math.min(stepIdx, steps.length - 1)
  const step = steps[safeIdx]
  const rail = useStepper(safeIdx)
  // The Overview still needs to know whether a generated name is in play. Read off the rows, which
  // are the only place the decision is made — the blade's per-row Group source.
  const generatesGroups = (rows: AssignmentRow[]) =>
    rows.some((r) => r.target === 'group' && r.groupSource === 'generated')
  const wantsAppGeneratedGroup = generatesGroups(d.appAssignments)
  const wantsUpdGeneratedGroup = d.updateEnabled && generatesGroups(d.updateAssignments)

  /** Every assignment the template publishes, app object first, for the Overview's table. Each
   *  generated target is named from the pattern it resolves to, which is the only thing that can
   *  tell an admin who the group about to be created holds.
   *
   *  Both objects render {Intent} from the row; their group patterns must differ, which the
   *  backend validates, because the updater's population must not be installed onto. */
  const assignmentTable = [
    ...(d.skipInstall
      ? []
      : assignmentReviewRows('App', d.appAssignments, groups, filters, (r) =>
          renderNamingPattern(d.groupPattern, { intent: intentToken(r.intent) }),
        )),
    ...(d.updateEnabled
      ? assignmentReviewRows('Update-only', d.updateAssignments, groups, filters, (r) =>
          renderNamingPattern(d.updateGroupPattern, { intent: intentToken(r.intent) }),
        )
      : []),
  ]
  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((p) => ({ ...p, [k]: v }))

  /** An assignment is only MANDATORY when the template follows new versions: a version repackaged
   *  and published unattended has to land somewhere, and there is nobody at the keyboard to point
   *  it at a group. A template that publishes only on demand is a legitimate configuration — the
   *  admin is right there and can assign the app in Intune — so requiring one would refuse it. */
  const assignmentRequired = d.followNewVersions

  /** Why the typed soak is not a number of days, or null. Only checked while the field is on
   *  screen — a template that does not follow new versions never waits for anything. */
  const soakError = d.followNewVersions ? soakDaysError(soakText) : null

  const setSoak = (text: string) => {
    setSoakText(text)
    const n = soakDaysValue(text)
    if (n !== null) set('soakDays', n)
  }

  // With the install skipped no app object is created, so an assignment for it cannot be acted
  // on and its absence is not a gap.
  const appUnassigned =
    !d.skipInstall && !d.appAssignments.some((r) => r.mode === 'include')
  const updateUnassigned = d.updateEnabled && !d.updateAssignments.some((r) => r.mode === 'include')
  /** Skipping the install without the update-only app publishes nothing at all. */
  const publishesNothing = d.skipInstall && !d.updateEnabled
  // The update-only app is the ONLY object when the install is skipped, so leaving it unassigned
  // reaches nobody -- required then regardless of whether the template follows new versions.
  const updateAssignmentRequired = assignmentRequired || d.skipInstall

  /** Each naming pattern in use, with its field label and the step it is edited on, so a pattern
   *  with a problem holds Continue on that step and is named by the label the admin sees. Checked
   *  from the draft rather than reported by the field, so a step jumped over is still checked.
   *  Both group fields are labelled "Group name" on their own step; the Overview names the
   *  update-only one in full. A group pattern is only checked while a generated group uses it. */
  const badPatterns = [
    { pattern: d.appPattern, scope: 'app', label: 'Application name',
      fullLabel: 'Application name', step: 'Metadata', used: true },
    { pattern: d.updateAppPattern, scope: 'app', label: 'Update-only application name',
      fullLabel: 'Update-only application name', step: 'Metadata', used: true },
    { pattern: d.groupPattern, scope: 'group', label: 'Group name',
      fullLabel: 'Group name', step: 'Install', used: !d.skipInstall && wantsAppGeneratedGroup },
    { pattern: d.updateGroupPattern, scope: 'group', label: 'Group name',
      fullLabel: 'Update-only group name', step: 'Update-only app', used: wantsUpdGeneratedGroup },
  ].filter((f) => f.used && validatePattern(f.pattern, f.scope as 'app' | 'group').problems.length > 0)

  /** Why Continue will not move, or null. The reference validates on Continue rather than at the
   *  end, so an unfinished step is named on the step that owns it. */
  const stepBlocker = (): string | null => {
    if (step === 'General' && !d.name.trim()) return 'Give the template a name'
    const badHere = badPatterns.find((f) => f.step === step)
    if (badHere) return `Fix the ${badHere.label} pattern — its problem is shown under the field.`
    if (step === 'Install' && assignmentRequired && appUnassigned)
      return 'Add at least one included assignment for the app, or turn off Follow new versions'
    if (step === 'Update-only app' && publishesNothing)
      return 'Skip install requires the update-only app — with both off this template publishes nothing.'
    if (step === 'Update-only app' && updateAssignmentRequired && updateUnassigned)
      return d.skipInstall
        ? 'Assign the update-only app — with the install skipped it is the only object published.'
        : 'Assign the update-only app, or turn it off'
    if (step === 'Automated updates' && soakError)
      return `Set how long to wait before publishing — 0 to ${SOAK_MAX} days`
    return null
  }

  /** Show why the wizard will not move on. The General rule is about one field, so it goes ON that
   *  field; the others are about the blade filling the step, and belong above the buttons. */
  const showBlocker = (why: string) => (step === 'General' ? setNameError(why) : setError(why))

  const goToStep = (i: number) => {
    if (i === safeIdx) return
    // Going back never validates, exactly as Back does not. Going forward runs the same rule
    // Continue runs, so a jump cannot skip what the step being left owns.
    if (i > safeIdx) {
      const why = stepBlocker()
      if (why) return showBlocker(why)
    }
    setError(null)
    setNameError(null)
    setVisited((v) => new Set(v).add(steps[i]))
    setStepIdx(i)
  }

  // Intune only auto-updates apps delivered as Available. Say so where the switch lives rather
  // than letting an admin believe Required-intent devices will be updated by it.
  const hasAvailable = d.appAssignments.some((r) => r.intent === 'available')

  // Shown on the Overview, where Save lives. Continue already stops each rule on the step that
  // owns it, so this is the cross-step summary — reaching Save with one of these outstanding means
  // a later step changed the answer. A row naming no group is the one the API rejects and no step
  // gate catches, so it is named here rather than surfacing as a save error.
  const saveBlockers = (() => {
    const out: string[] = []
    // Unreachable while Continue gates step 1, but it is what disables Save — an unstated reason
    // for a dead button is the thing this list exists to prevent.
    if (!onApply && !d.name.trim()) out.push('The template has no name.')
    if (assignmentRequired && appUnassigned) {
      out.push('The app has no included assignment, and this template follows new versions.')
    }
    if (publishesNothing) {
      out.push('Skip install is on but the update-only app is off, so this template publishes nothing.')
    }
    if (updateAssignmentRequired && updateUnassigned) {
      out.push(d.skipInstall
        ? 'The update-only app has no assignment, and with the install skipped it is the only object published.'
        : 'The update-only app has no assignment. Assign it, or turn it off.')
    }
    if ((d.skipInstall ? [] : d.appAssignments).concat(d.updateEnabled ? d.updateAssignments : [])
        .some((r) => r.target === 'group' && r.groupSource === 'existing' && !r.groupId)) {
      out.push('One assignment targets a group but no group is chosen.')
    }
    if (soakError) {
      out.push(`Wait before publishing is not a whole number of days between 0 and ${SOAK_MAX}.`)
    }
    for (const f of badPatterns) {
      out.push(`The ${f.fullLabel} pattern has a problem. Fix it on the ${f.step} step.`)
    }
    return out
  })()

  /** The same two facts when nothing is published unattended: worth stating, since an unassigned
   *  app reaches nobody, but not a reason to refuse a save. */
  const saveNotes = assignmentRequired
    ? []
    : [
        appUnassigned &&
          'The app has no assignment — it is created in Intune, but no device receives it until you assign it there.',
        d.skipInstall &&
          'The install is skipped, so only the update-only app is published — the app is patched where it is already installed and never introduced.',
        updateUnassigned &&
          'The update-only app has no assignment, so it patches nothing until you assign it.',
      ].filter(Boolean) as string[]

  const save = async () => {
    setSaving(true)
    setError(null)
    try {
      const config = toConfig(d)
      const body = {
        name: d.name.trim(),
        // A blank description falls back to what the template DOES, so the list never shows a
        // nameless row -- the reference derives the same sentence on save.
        description:
          d.description.trim() ||
          intuneSummary({ config } as any, { omitPattern: true }),
        config,
        // Lets the backend fully enforce the audience rule: whether a group holds users or
        // devices lives on the group, not the assignment, so without this the check degrades to
        // a warning.
        group_audiences: Object.fromEntries(groups.map((g) => [g.id, g.memberType])),
      }
      if (template) await updateIntuneTemplate(template.id, body)
      else await createIntuneTemplate(body)
      onSaved()
      onClose()
    } catch (e: any) {
      setError(errorText(e, 'Could not save the template.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      {/* Was `max-w-3xl max-h-full overflow-y-auto` plus card chrome — leftovers from when this
          wizard was a modal. In the content area they capped a 6-step form at 48rem and gave it a
          second scrollbar inside the page's own. The wizard is a page now: full width, one
          scrollbar, fields aligned to the page header. */}
      <div className="w-full py-2">
        <h3 className="text-lg font-semibold text-theme-text-primary mb-1">
          {template ? 'Edit' : 'New'} Intune deployment template
        </h3>
        <p className="text-xs text-theme-text-tertiary mb-4">
          Who gets the app and how. Applied when you publish; editing this later never changes an
          app you already published.
        </p>

        <Stepper
          id={rail.id}
          steps={steps.map((label) => ({ key: label, label }))}
          current={safeIdx}
          onGoTo={goToStep}
          /* Editing an existing template — or adjusting one for a single deployment — starts from
             settings that are already complete, so every step is one click away. Creating one:
             only what has been walked through. */
          canGoTo={isEdit ? undefined : (i) => visited.has(steps[i])}
          ariaLabel="Deployment template steps"
        />

        <div {...rail.panelProps}>
        {step === 'General' && (
          <>
            <Field label="Name" error={nameError}>
              <input
                className={`${inputCls} ${nameError ? '!border-red-500' : ''}`}
                value={d.name}
                onChange={(e) => {
                  set('name', e.target.value)
                  if (nameError) setNameError(null)
                }}
                placeholder="Broad rollout — EMEA"
                autoFocus
              />
            </Field>
            <Field label="Description" hint="Shown in the template list.">
              <input
                className={inputCls}
                value={d.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Who this template delivers to, and how"
              />
            </Field>
          </>
        )}

        {step === 'Metadata' && (
          <>
            {/* --- template-level: the same for every app this template publishes --- */}
            <section>
              <h4 className="text-sm font-medium text-theme-text-primary mb-2">
                App information — set by this template
              </h4>
              <div className="grid gap-x-4 sm:grid-cols-2">
                <Field label="Owner" hint="The team accountable for the app.">
                  <input
                    className={inputCls}
                    value={d.owner}
                    onChange={(e) => set('owner', e.target.value)}
                    placeholder="Workplace Services"
                  />
                </Field>
                <Field label="Developer" hint="Who packaged it — not the software vendor.">
                  <input
                    className={inputCls}
                    value={d.developer}
                    onChange={(e) => set('developer', e.target.value)}
                    placeholder={DEFAULT_DEVELOPER}
                  />
                </Field>
                <Field label="Information URL">
                  <input
                    className={inputCls}
                    value={d.informationUrl}
                    onChange={(e) => set('informationUrl', e.target.value)}
                    placeholder="https://intranet.example.com/apps"
                  />
                </Field>
                <Field label="Privacy URL">
                  <input
                    className={inputCls}
                    value={d.privacyUrl}
                    onChange={(e) => set('privacyUrl', e.target.value)}
                    placeholder="https://intranet.example.com/privacy"
                  />
                </Field>
              </div>

              {/* F-10b: `notes` was in the draft and written to intuneMetadata.notes, with no
                  input anywhere — the mock has one (its DeploymentTemplateWizard.tsx:534). */}
              <Field label="Notes" hint="Free text on the app's Properties blade.">
                <textarea
                  className={`${inputCls} min-h-[72px] resize-y`}
                  value={d.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  placeholder={`Packaged by ${DEFAULT_DEVELOPER}. Raise issues with Workplace Services.`}
                />
              </Field>

              {/* Chips, not the comma-separated text box this used to be. Categories are matched
                  BY NAME when they are POSTed to /mobileApps/{id}/categories/$ref, so free text
                  is a name Intune will not match. The names are the tenant's own — see
                  categoryOptions. */}
              {/* "Category (Intune)" — the catalog has a category of its own, and the two are
                  different vocabularies. Named for the system it comes from rather than for this
                  product, which would be wrong the day the product is renamed. */}
              <Field
                label="Category (Intune)"
                hint={
                  categoriesLoading
                    ? 'Reading this tenant’s categories from Intune…'
                    : categoriesAvailable
                      ? "How the app is filed in the Company Portal. Your Intune tenant's own categories — add more in Intune under Apps > Categories."
                      : 'Could not read the categories from Intune. Only what this template already carries is shown.'
                }
              >
                {categoryOptions.length === 0 ? (
                  <p className="text-xs text-theme-text-tertiary">
                    {categoriesKnown
                      ? 'This tenant has no app categories in Intune.'
                      : 'No categories to show.'}
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {categoryOptions.map(({ name, inTenant }) => {
                      const on = d.categories.includes(name)
                      return (
                        <button
                          key={name}
                          type="button"
                          title={
                            inTenant
                              ? undefined
                              : 'Not a category in this Intune tenant — it will not be applied when the app is published.'
                          }
                          onClick={() =>
                            set(
                              'categories',
                              on ? d.categories.filter((x) => x !== name) : [...d.categories, name]
                            )
                          }
                          className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                            !inTenant
                              ? 'border-amber-500/60 bg-amber-500/10 text-amber-500 line-through'
                              : on
                                ? 'border-blue-500 bg-blue-500/15 text-blue-400'
                                : 'border-theme-border-primary bg-theme-secondary text-theme-text-tertiary hover:text-theme-text-primary'
                          }`}
                        >
                          {name}
                        </button>
                      )
                    })}
                  </div>
                )}
                {missingCategories.length > 0 && (
                  <p className="mt-1.5 text-xs text-amber-500">
                    {missingCategories.join(', ')} {missingCategories.length === 1 ? 'is' : 'are'}{' '}
                    not a category in this Intune tenant, so publishing will not apply{' '}
                    {missingCategories.length === 1 ? 'it' : 'them'}. Create the category in Intune,
                    or deselect it here.
                  </p>
                )}
              </Field>

              <div className="space-y-2.5 rounded-lg border border-theme-border-primary bg-theme-secondary p-4 mb-4">
                <Checkbox
                  checked={d.featured}
                  onChange={(v) => set('featured', v)}
                  label="Featured app"
                  hint="Shows the app on the Company Portal home page."
                />
                <Checkbox
                  checked={d.allowUninstallFromCompanyPortal}
                  onChange={(v) => set('allowUninstallFromCompanyPortal', v)}
                  label="Allow available uninstallation in Company Portal"
                  hint="Gives the user an Uninstall button on apps they can see."
                />
              </div>
            </section>

            {/* --- per-app: read from the package, never stored on a template (mock :589-618) --- */}
            <section>
              <h4 className="text-sm font-medium text-theme-text-primary mb-2">Filled in per app</h4>
              <p className="mb-3 rounded-lg border border-theme-border-primary bg-theme-secondary px-4 py-2.5 text-xs text-theme-text-tertiary">
                These come from the application itself when it is packaged, so a template does not
                carry them — one template would otherwise rename and re-describe every app it
                touches.
              </p>
              <div className="grid gap-x-4 sm:grid-cols-2">
                <Field
                  label="App name"
                  hint="Generated from the app name pattern below — read-only at packaging time."
                >
                  <input
                    className={`${inputCls} font-mono text-[13px]`}
                    value={renderNamingPattern(d.appPattern, NO_APP_YET)}
                    disabled
                    readOnly
                  />
                </Field>
                {/* The variable, not a sample value: the field IS the substitution, and a concrete
                    brand name would read as something we had chosen (mock :602-606). */}
                <Field label="Publisher" hint="e.g. Contoso">
                  <input
                    className={`${inputCls} font-mono text-[13px]`}
                    value="{Publisher}"
                    disabled
                    readOnly
                  />
                </Field>
                <div className="sm:col-span-2">
                  <Field
                    label="Description"
                    hint="e.g. Contoso App is a file archiver."
                  >
                    <input
                      className={`${inputCls} font-mono text-[13px]`}
                      value="{Description}"
                      disabled
                      readOnly
                    />
                  </Field>
                </div>
              </div>
            </section>

            {/* --- the name builder (mock :621-641) ---
                F-1: only the APP patterns live here. An app object is always created, so these are
                always used. The GROUP patterns moved to the steps that decide whether a generated
                group is wanted at all — configuring a name you may never need, two steps before the
                choice that decides it, was the complaint. */}
            <section className="border-t border-theme-border-primary pt-4">
              <h4 className="text-sm font-medium text-theme-text-primary mb-2">Naming</h4>
              <p className="mb-3 text-xs text-theme-text-tertiary">
                Object names are generated, not typed, so a tenant stays consistent without anyone
                reading a wiki. Token availability differs per object — the picker only offers what
                is valid.
              </p>
              <NamingPatternField
                label="Application name"
                scope="app"
                hint="The name of the app object created in Intune."
                value={d.appPattern}
                onChange={(v) => set('appPattern', v)}
                previewValues={namePreview}
              />
              <NamingPatternField
                label="Update-only application name"
                scope="app"
                hint="The second app object, which only patches devices that already have the app. Give it a visible marker so the two are told apart in a list."
                value={d.updateAppPattern}
                onChange={(v) => set('updateAppPattern', v)}
                previewValues={namePreview}
              />
            </section>
          </>
        )}

        {step === 'Advanced' && (
          <AdvancedSettings
            value={d.requirements}
            onChange={(v) => set('requirements', v)}
            program={{ restartBehaviour: d.restartBehaviour, returnCodes: d.returnCodes }}
            onProgramChange={(p) => {
              set('restartBehaviour', p.restartBehaviour)
              set('returnCodes', p.returnCodes)
            }}
          />
        )}

        {step === 'Install' && (
          /* The group pattern is rendered INSIDE the blade, so each of the two objects configures
             its own; out here it could only ever edit the app object's. */
          <>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-theme-border-primary bg-theme-secondary px-4 py-3 mb-4">
            <div>
              <p className="text-sm font-medium text-theme-text-primary">Skip install</p>
              <p className="text-xs text-theme-text-tertiary">
                Don’t create the install app object in Intune. The app is patched where it is
                already installed and never introduced.
              </p>
            </div>
            <Toggle
              checked={d.skipInstall}
              onChange={(v) => set('skipInstall', v)}
              label="Skip install"
            />
          </div>

          <h4 className="mb-1 text-sm font-semibold text-theme-text-primary">The app</h4>
          {/* The rows are kept on the draft while this is hidden, so turning the toggle back off
              restores what was entered rather than silently discarding it. */}
          {d.skipInstall ? (
            <p className="rounded-lg border border-dashed border-theme-border-primary px-4 py-6 text-center text-[13px] text-theme-text-tertiary">
              No install app object is created, so there is nothing to assign here. Assign the
              update-only app on the next step.
            </p>
          ) : (
            <AssignmentBlade
              value={d.appAssignments}
              onChange={(rows) => set('appAssignments', rows)}
              note={
                <p className="rounded-lg border border-theme-border-primary bg-theme-primary px-4 py-2.5 text-xs text-theme-text-tertiary">
                  Targeting for the main app object — the one that installs the app, offers it in the
                  Company Portal, or removes it. Each row either points at an existing Entra group or
                  has its group created from the template’s group pattern.
                </p>
              }
              intents={['required', 'available', 'uninstall']}
              groups={groups}
              filters={filters}
              allowGeneratedGroups
              groupPattern={d.groupPattern}
              onGroupPatternChange={(v) => set('groupPattern', v)}
              previewValues={rowPreview}
            />
          )}
          </>
        )}

        {step === 'Update-only app' && (
          <>
            <div className="flex items-center justify-between gap-4 rounded-lg border border-theme-border-primary bg-theme-secondary px-4 py-3 mb-4">
              <div>
                <p className="text-sm font-medium text-theme-text-primary">
                  Deploy an update-only app
                </p>
                <p className="text-xs text-theme-text-tertiary">
                  A second Intune app that only updates devices that already have the app; it never
                  installs the app on a device without it. A requirement rule checks for the existing
                  install.
                </p>
              </div>
              <Toggle
                checked={d.updateEnabled}
                onChange={(v) => set('updateEnabled', v)}
                label="Deploy an update-only app"
              />
            </div>

            {d.updateEnabled ? (
              <>
                <p className="text-xs text-theme-text-tertiary mb-3">
                  Published as{' '}
                  <span className="font-mono text-theme-text-secondary">
                    {renderNamingPattern(d.updateAppPattern, NO_APP_YET)}
                  </span>
                  . Its assignments are separate from the app's — patching a broad population is safe
                  precisely because it installs nothing new. Each new version gets its own
                  update-only app; the previous one is retired once the replacement is live.
                </p>

                {/* Above the assignment recommendation on purpose: it decides what "All devices"
                    actually reaches. Two options rather than a toggle, because the narrow one is a
                    real policy — exact, and unable to touch anything this platform did not install
                    — not the absence of one.

                    It moves the object's REQUIREMENT and its DETECTION together. Widening only the
                    requirement is what made Intune install a packaged version over a device that
                    already had a newer one: applicable, because the product is in Add/Remove
                    Programs, and not detected, because our install marker is not. */}
                <div className="mb-4 rounded-lg border border-theme-border-primary bg-theme-secondary px-4 py-3">
                  <p className="mb-2 text-sm font-medium text-theme-text-primary">
                    Which installs does it patch?
                  </p>
                  <div className="space-y-2">
                    <label className="flex cursor-pointer items-start space-x-3">
                      <input
                        type="radio"
                        name="updateMatchUnmanaged"
                        checked={!d.updateMatchUnmanaged}
                        onChange={() => set('updateMatchUnmanaged', false)}
                        className="mt-1"
                      />
                      <div>
                        <div className="text-sm font-medium text-theme-text-primary">
                          Installs from Agentic Packr
                        </div>
                        <div className="text-xs text-theme-text-tertiary">
                          Only devices where Agentic Packr installed this app. Exact — it cannot
                          touch anything else.
                        </div>
                      </div>
                    </label>
                    <label className="flex cursor-pointer items-start space-x-3">
                      <input
                        type="radio"
                        name="updateMatchUnmanaged"
                        checked={d.updateMatchUnmanaged}
                        onChange={() => set('updateMatchUnmanaged', true)}
                        className="mt-1"
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-theme-text-primary">
                            Any install of this app
                          </span>
                          {/* Said plainly, because it is true: Windows has no reliable identity
                              for "this product", so the match is the app's Add/Remove Programs
                              name and it can be wrong in both directions. */}
                          <span className="rounded bg-theme-tertiary px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-theme-text-tertiary">
                            Best effort
                          </span>
                        </div>
                        <div className="text-xs text-theme-text-tertiary">
                          Also patches copies installed by hand, by an older tool, or in an OEM
                          image. Matched on the app's name in Add/Remove Programs. A device already
                          on a newer version is left alone.
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Removal is the one direction where a wide match is destructive: the same
                    predicate that decides who gets PATCHED decides who gets UNINSTALLED, because
                    detection is a property of the app object and not of the assignment. Stated
                    where the choice is made — the assignment step cannot know the reach, and a
                    person who wanted to tidy up their own deployments would otherwise find out by
                    removing someone's own copy of the app. */}
                {d.updateMatchUnmanaged &&
                  d.updateAssignments.some((r) => r.intent === 'uninstall' && r.mode === 'include') && (
                    <p className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/[0.06] px-4 py-2.5 text-xs text-theme-text-secondary">
                      <span className="font-medium text-theme-text-primary">
                        The Uninstall row below removes any copy this matches
                      </span>{' '}
                      — including copies Agentic Packr did not install. Choose “Installs from
                      Agentic Packr” above to limit removal to your own deployments.
                    </p>
                  )}

                {/* The whole point of this object is breadth: it cannot introduce the app, so
                    narrowing it only leaves devices unpatched (mock :729-735). */}
                <p className="mb-4 rounded-lg border border-blue-500/30 bg-blue-500/[0.06] px-4 py-2.5 text-xs text-theme-text-secondary">
                  <span className="font-medium text-theme-text-primary">
                    Recommended: assign to All devices
                  </span>{' '}
                  (or All users). The update-only app is a catch-all — it patches whatever is already
                  installed and installs nothing new, so a narrow target just leaves machines behind.
                </p>
                <AssignmentBlade
                  value={d.updateAssignments}
                  onChange={(rows) => set('updateAssignments', rows)}
                  /* Nobody browses the Company Portal for an updater, so Available is never
                     offered. Uninstall only when the install is skipped: this is then the ONLY
                     object published, so it is the only one that can remove the product, and its
                     requirement rule already limits it to devices that have it. Alongside an
                     install app object, removing the app is that object's job. */
                  intents={d.skipInstall ? ['required', 'uninstall'] : ['required']}
                  groups={groups}
                  filters={filters}
                  allowGeneratedGroups
                  /* Its OWN pattern, kept separate on purpose: the population that may be patched
                     is wider than the one the app may be installed onto, so the two must never
                     share a group. */
                  groupPattern={d.updateGroupPattern}
                  onGroupPatternChange={(v) => set('updateGroupPattern', v)}
                  previewValues={rowPreview}
                />
              </>
            ) : (
              <p className="rounded-lg border border-dashed border-theme-border-primary px-4 py-6 text-center text-[13px] text-theme-text-tertiary">
                Only the app itself is published. Devices that already have it are updated by the
                app's own Required assignment, if it has one.
              </p>
            )}
          </>
        )}

        {step === 'Automated updates' && (
          <>
            <p className="mb-4 rounded-lg border border-theme-border-primary bg-theme-secondary px-4 py-2.5 text-xs text-theme-text-tertiary">
              What happens when the source publishes a new version of the app. These settings apply
              to the next version, not the one being deployed now. Each deployment keeps the policy
              it was deployed with: a change here reaches a deployment the next time it is deployed
              with this template, and can be changed for one deployment on its page.
            </p>

            <div className="rounded-xl border border-theme-border-primary bg-theme-primary p-4 mb-4">
              <div className="flex items-center justify-between gap-4">
                <div className="pr-2">
                  <p className="text-sm font-medium text-theme-text-primary">Follow new versions</p>
                  <p className="text-xs text-theme-text-tertiary">
                    Repackage and publish automatically when the catalog has a newer version. Leave
                    off to update on your own schedule.
                  </p>
                </div>
                <Toggle
                  checked={d.followNewVersions}
                  onChange={(v) => set('followNewVersions', v)}
                  label="Follow new versions"
                />
              </div>

              {d.followNewVersions && (
                <div className="mt-3 border-t border-theme-border-primary pt-3">
                  <Field
                    label="Wait before publishing"
                    hint={`Waiting a few days after a vendor release lets a bad release surface before it reaches your estate. 0 publishes as soon as the new version is packaged; ${SOAK_MAX} days is the longest wait.`}
                    error={soakError}
                  >
                    <SoakDaysInput text={soakText} onChange={setSoak} inputClassName={inputCls} />
                  </Field>
                </div>
              )}
            </div>

            {/* Its own card, always visible — NOT nested under supersedence, which is where this
                used to live. How many app objects to retain is a tenant-hygiene decision that
                applies whether or not the new version supersedes the old one (mock :822-846). */}
            <div className="rounded-xl border border-theme-border-primary bg-theme-primary p-4 mb-4">
              <Field
                label="Versions to keep in Intune"
                hint="Older install-app objects kept after a new version publishes. Three gives you room to roll back past a bad release without hand-rebuilding the package; keeping none leaves the tenant tidy. Update-only apps are not kept — the previous one is retired as soon as its replacement is live."
              >
                <Select
                  value={String(d.keepVersions)}
                  onChange={(v) => set('keepVersions', Number(v))}
                  options={KEEP_OPTIONS}
                />
              </Field>
            </div>

            <div className="rounded-xl border border-theme-border-primary bg-theme-primary p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="pr-2">
                  <p className="text-sm font-medium text-theme-text-primary">
                    Supersede the previous version{' '}
                    <span className="text-theme-text-tertiary">— install app only</span>
                  </p>
                  <p className="text-xs text-theme-text-tertiary">
                    Links the new install app to the old one so devices move forward instead of
                    collecting versions. The update-only app is never superseded — a new one is
                    published for each version and the previous one is retired once the new one is
                    live, so only one is ever assigned.
                  </p>
                  {!hasAvailable && (
                    <p className="mt-1.5 text-xs text-theme-text-tertiary">
                      This template only targets Required, which already installs the newest version
                      on its own — supersedence is optional here, and mainly buys you a rollback
                      path.
                    </p>
                  )}
                </div>
                <Toggle
                  checked={d.supersede}
                  onChange={(v) => set('supersede', v)}
                  label="Supersede the previous version"
                />
              </div>

              {d.supersede && (
                <div className="mt-3 space-y-3 border-t border-theme-border-primary pt-3">
                  <Field
                    label="Previous version"
                    hint={
                      d.uninstallPrevious
                        ? 'Replacement: Intune uninstalls the old version, then installs the new one.'
                        : 'Update: the new installer upgrades in place — Intune sends no uninstall.'
                    }
                  >
                    <Select
                      value={d.uninstallPrevious ? 'uninstall' : 'update'}
                      onChange={(v) => set('uninstallPrevious', v === 'uninstall')}
                      options={[
                        { value: 'update', label: 'Let the installer update it (recommended)' },
                        { value: 'uninstall', label: 'Uninstall it first' },
                      ]}
                    />
                  </Field>

                  {/* Not a choice: auto-update is the reason to set supersedence at all, so it is
                      always on rather than a switch someone forgets to flip (mock :899-913). */}
                  <div className="border-t border-theme-border-primary pt-3">
                    <p className="text-sm font-medium text-theme-text-primary">
                      Auto-update apps installed from the Company Portal
                    </p>
                    <p className="mt-0.5 text-xs text-theme-text-tertiary">
                      Always on — users who installed the old version themselves get the new one
                      without asking. Microsoft's guidance puts this at two device check-ins,
                      commonly 8–16 hours.
                    </p>
                  </div>

                  {/* Microsoft's constraint, surfaced where it bites rather than in a doc. */}
                  {!hasAvailable && (
                    <p className="text-xs text-theme-text-tertiary">
                      This template has no Available assignment, so auto-update has nothing to act on
                      here — Intune only applies it to apps a user installed from the Company Portal.
                      Required devices get the new version from the assignment itself.
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {step === 'Overview' && (
          /* F-2: this printed JSON.stringify(toConfig(d)) — the saved object, which asks the
             reviewer to read the model instead of the decision. Now a labelled read-out in the
             mock's five sections and its order (its OverviewStep :959-1079). Patterns are
             RESOLVED, not printed as tokens: a review that shows {AppName} has not told you what
             the app will be called. */
          <div>
            <OvSection title="Template">
              <Line k="Name" v={d.name || '—'} />
              <Line k="Description" v={d.description || '—'} />
            </OvSection>

            <OvSection title="Advanced">
              <Line
                k="Architecture"
                v={
                  d.requirements.architectures.length
                    ? d.requirements.architectures.join(', ')
                    : 'Any — not checked'
                }
              />
              <Line k="Minimum OS" v={d.requirements.minimumOs || '—'} />
              <Line k="Hardware" v={hardwareSummary(d.requirements)} />
              <Line
                k="Extra rules"
                v={
                  d.requirements.rules.length
                    ? `${d.requirements.rules.length} rule${d.requirements.rules.length === 1 ? '' : 's'}`
                    : 'None'
                }
              />
              <Line
                k="Restart"
                v={
                  RESTART_BEHAVIOURS.find((o) => o.value === d.restartBehaviour)?.label ??
                  'Determine behaviour based on return codes'
                }
              />
              <Line k="Return codes" v={`${d.returnCodes.length} mapped`} />
            </OvSection>

            {/* The patterns as typed, every token visible: {DeploymentName} renders empty for a
                package's first deployment, so rendering it here would hide it. */}
            <OvSection
              title="Names it generates"
              note={
                [d.appPattern, d.updateAppPattern, d.groupPattern, d.updateGroupPattern].some(
                  usesDeploymentName,
                )
                  ? 'Tokens are filled in from the package at packaging time, and the names are read-only there. {DeploymentName} is empty for a package\'s first deployment, and the separator before it is dropped.'
                  : 'Tokens are filled in from the package at packaging time, and the names are read-only there.'
              }
            >
              <Line
                k="App"
                v={d.skipInstall ? 'Not published — install skipped' : d.appPattern || '—'}
                mono={!d.skipInstall}
              />
              <Line
                k="Update-only app"
                v={d.updateEnabled ? d.updateAppPattern || '—' : 'Not published'}
                mono={d.updateEnabled}
              />
              <Line
                k="Group"
                v={d.skipInstall ? 'Not published — install skipped' : d.groupPattern || '—'}
                mono={!d.skipInstall}
              />
              <Line
                k="Update-only group"
                v={d.updateEnabled ? d.updateGroupPattern || '—' : 'Not published'}
                mono={d.updateEnabled}
              />
            </OvSection>

            <OvSection title="App information">
              <Line k="Owner" v={d.owner || '—'} />
              <Line k="Developer" v={d.developer || '—'} />
              <Line
                k="Category (Intune)"
                v={
                  d.categories.length ? (
                    <>
                      {d.categories.join(', ')}
                      {missingCategories.length > 0 && (
                        <span className="text-amber-500">
                          {' '}
                          — {missingCategories.join(', ')} not in this Intune tenant, will not be
                          applied
                        </span>
                      )}
                    </>
                  ) : (
                    'None'
                  )
                }
              />
              <Line k="Featured app" v={d.featured ? 'Yes' : 'No'} />
              <Line
                k="Uninstall in Company Portal"
                v={d.allowUninstallFromCompanyPortal ? 'Allowed' : 'Not allowed'}
              />
              <Line k="Information URL" v={d.informationUrl || '—'} />
              <Line k="Privacy URL" v={d.privacyUrl || '—'} />
              <Line k="Notes" v={d.notes || '—'} />
            </OvSection>

            <OvSection
              title="Assignments"
              dl={false}
              note={[
                d.skipInstall
                  ? 'The install is skipped: no install app object is created, so only the update-only app is published and the app is never introduced to a device that does not have it.'
                  : null,
                d.updateEnabled ? null : 'The update-only app is not published.',
                assignmentTable.some((r) => r.generated)
                  ? 'A target marked new does not exist yet — publishing the app creates it.'
                  : null,
              ]
                .filter(Boolean)
                .join(' ')}
            >
              <AssignmentReviewTable rows={assignmentTable} />
            </OvSection>

            <OvSection title="Automated updates">
              <Line
                k="New versions"
                v={
                  d.followNewVersions
                    ? d.soakDays === 0
                      ? 'Published immediately'
                      : `Published ${d.soakDays} day${d.soakDays === 1 ? '' : 's'} after release`
                    : 'Manual — not followed'
                }
              />
              <Line
                k="Versions kept"
                v={d.keepVersions === 0 ? 'Only the latest' : `${d.keepVersions} previous`}
              />
              <Line
                k="Supersedence"
                v={
                  d.supersede
                    ? d.uninstallPrevious
                      ? 'Replaces the previous version (uninstall first)'
                      : 'Updates the previous version in place'
                    : 'Off'
                }
              />
            </OvSection>
          </div>
        )}
        </div>

        {error && <p className="mt-3 text-xs text-red-400">{error}</p>}

        {/* Named, not silent: a disabled Save with no stated reason is the complaint this wizard
            already collected once. On the Overview ONLY — it is the summary of why Save is off, and
            on step 1 it read as a to-do list for fields four steps away that no answer could clear. */}
        {!onApply && step === 'Overview' && saveBlockers.length > 0 && (
          <div className="mt-3">
            <p className="text-xs font-medium text-amber-400">You can't save yet because:</p>
            <ul className="mt-1 space-y-1">
              {saveBlockers.map((b) => (
                <li key={b} className="text-xs text-amber-400">{b}</li>
              ))}
            </ul>
          </div>
        )}
        {!onApply && step === 'Overview' && saveNotes.length > 0 && (
          <ul className="mt-3 space-y-1">
            {saveNotes.map((n) => (
              <li key={n} className="text-xs text-theme-text-tertiary">{n}</li>
            ))}
          </ul>
        )}

        <div className="flex justify-between mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-theme-text-tertiary hover:text-theme-text-primary"
          >
            Cancel
          </button>
          <div className="flex gap-2">
            {safeIdx > 0 && (
              <button
                type="button"
                onClick={() => goToStep(safeIdx - 1)}
                className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-secondary"
              >
                Back
              </button>
            )}
            {safeIdx < steps.length - 1 ? (
              <button
                type="button"
                onClick={() => goToStep(Math.min(safeIdx + 1, steps.length - 1))}
                className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-primary"
              >
                Continue
              </button>
            ) : onApply ? (
              /* F-7 apply mode: hand the resolved config back, save nothing. No name is required
                 because nothing is being stored under one. */
              <button
                type="button"
                onClick={() => onApply(toConfig(d))}
                className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-50"
              >
                Apply to this deployment
              </button>
            ) : (
              <button
                type="button"
                disabled={saving || saveBlockers.length > 0}
                onClick={save}
                className="px-3 py-1.5 text-sm border border-theme-border-primary rounded-lg text-theme-text-primary disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save template'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeploymentTemplateWizard
